// @flow
/* global window */

import * as React from 'react';

import { useDispatch } from 'react-redux';

import type { PreFetchReturn } from '@gandi/app-runner/server';
import { selectCartUUID } from '@gandi/cart';
import type { NotifyAction } from '@gandi/notifications';

import { contactbook } from '@gandi/redux-resource-contactbook';
import type { FetchOwnerContactExtraParamsAction } from '@gandi/redux-resource-contactbook';
import { actions as templateActions } from '@gandi/template2';
import type { ProductType, Process } from '@gandi/template2';
import { useTranslator, type TranslatorProps } from '@gandi/translate';
import { Button, Base, Pill } from '@gandi/ui';

import type { GandiLogger } from 'gandi.redux-logger';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { Layout } from '../../../components/Layout';
import CountryLoader from '../../../shared/CountryLoader';
import type { WithRouterProps, PreFetchArgs, FullState } from '../../../types';

import { loadFavoriteContacts } from '../modules/actions';
import type { UpdateDomainsPayloadProps } from '../modules/actions';

import {
  selectHasDomainCreate,
  selectHasDomainTransfer,
  selectSetupOwner,
  selectShouldFetchOneDefaultTemplate,
} from '../modules/selectors';

import Contacts from './components/Contacts';
import Template from './components/Template';

type Params = {
  process?: string,
};

export type InjectedProps = {
  ...WithRouterProps<Params>,
  logger: GandiLogger,
};

export type DispatchProps = {
  cleanUpAllDrafts: () => void,
  getBaseContact: (
    translator: TranslatorProps,
  ) => Promise<(void => void) | void>,
  notifyDanger: NotifyAction,
  updateDomainsPayload: UpdateDomainsPayloadProps => Promise<any>,
  fetchOwnerContactExtraParams: FetchOwnerContactExtraParamsAction<any>,

  fetchDefaultTemplate: ({
    productType: ProductType,
    process: Process,
    sharingId: string,
  }) => Promise<void | boolean>,
};

export type StateProps = {
  hasDomainCreate: boolean,
  hasDomainTransfer: boolean,
  isPayloadInvalid: boolean,
  shouldFetchFavoriteContacts: boolean,
  setupOwner: string,
  hasCreateDraft: boolean,
  hasTransferDraft: boolean,
  shouldFetchDefaultTemplateCreate: boolean,
  shouldFetchDefaultTemplateTransfer: boolean,
  shouldFetchOwnerExtraParams: boolean,
};

export type Props = { ...InjectedProps, ...StateProps, ...DispatchProps };

function Presenter({
  params,
  navigate,
  cleanUpAllDrafts,
  hasDomainCreate,
  hasDomainTransfer,
  getBaseContact,
  logger,
  updateDomainsPayload,
  notifyDanger,
  isPayloadInvalid,
  hasCreateDraft,
  hasTransferDraft,
  shouldFetchFavoriteContacts,
  fetchOwnerContactExtraParams,
  setupOwner,

  shouldFetchDefaultTemplateCreate,
  shouldFetchDefaultTemplateTransfer,
  shouldFetchOwnerExtraParams,
  fetchDefaultTemplate,
}: Props): React.Node {
  const dispatch = useDispatch();
  const translator = useTranslator();
  const { __ } = translator;
  const cartId = useAppSelector(selectCartUUID);

  // owner
  const [owner, setOwner] = React.useState();

  React.useEffect(() => {
    (async function asyncLoader() {
      if (!owner) {
        const baseContact = await getBaseContact(translator);
        setOwner(baseContact);
      }
    })();
  }, [owner, setOwner, getBaseContact, translator]);

  // scroll top
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // default template create
  React.useEffect(() => {
    if (shouldFetchDefaultTemplateCreate) {
      fetchDefaultTemplate({
        productType: 'domain',
        process: 'create',
        sharingId: setupOwner,
      });
    }
  }, [shouldFetchDefaultTemplateCreate, setupOwner, fetchDefaultTemplate]);

  // default template transfer
  React.useEffect(() => {
    if (shouldFetchDefaultTemplateTransfer) {
      fetchDefaultTemplate({
        productType: 'domain',
        process: 'transfer',
        sharingId: setupOwner,
      });
    }
  }, [shouldFetchDefaultTemplateTransfer, setupOwner, fetchDefaultTemplate]);

  // owner extra params
  // required for the warning box
  React.useEffect(() => {
    if (shouldFetchOwnerExtraParams) {
      fetchOwnerContactExtraParams(setupOwner);
    }
  }, [shouldFetchOwnerExtraParams, fetchOwnerContactExtraParams, setupOwner]);

  // contacts
  React.useEffect(() => {
    if (shouldFetchFavoriteContacts) {
      dispatch(loadFavoriteContacts(setupOwner));
    }
  }, [setupOwner, shouldFetchFavoriteContacts, dispatch]);

  React.useEffect(() => {
    fetchOwnerContactExtraParams(setupOwner);
  }, [setupOwner, fetchOwnerContactExtraParams]);

  // isWorking
  const [isWorking, setIsWorking] = React.useState(false);

  // pill selection
  const [createSelected, setCreateSelected] = React.useState(() => {
    if (params.process === 'create' && hasDomainCreate) {
      return true;
    }

    if (params.process === 'transfer' && hasDomainTransfer) {
      return false;
    }

    //  make transfer tab by default
    return !hasDomainTransfer;
  });

  React.useEffect(() => {
    // handles case when user is removing domains from the cart
    if (!hasDomainCreate && !hasDomainTransfer) {
      navigate(`/cart/${cartId}/overview`);
    }

    if (!hasDomainCreate && createSelected) {
      setCreateSelected(false);
    }

    if (!hasDomainTransfer && !createSelected) {
      setCreateSelected(true);
    }
  }, [hasDomainCreate, hasDomainTransfer, cartId, createSelected, navigate]);

  const process = createSelected ? 'create' : 'transfer';

  return (
    <Layout currentStep={1}>
      <Base marginBottom="base">
        <CountryLoader />

        {hasDomainTransfer && (
          <Pill
            selected={!createSelected}
            onClick={() => setCreateSelected(false)}
            key="transfer-tab-pill"
            marginRight="small"
          >
            {__('Domains Transfer')}
          </Pill>
        )}
        {hasDomainCreate && (
          <Pill
            selected={createSelected}
            onClick={() => setCreateSelected(true)}
            key="registration-tab-pill"
          >
            {__('Domains Registrations')}
          </Pill>
        )}
      </Base>

      <Template process={process} />

      <Contacts owner={owner} process={process} />

      <Button
        type="button"
        disabled={!owner || false /* `false` for render server side */}
        loading={isWorking}
        onClick={async () => {
          setIsWorking(true);
          try {
            if (
              (isPayloadInvalid || hasCreateDraft || hasTransferDraft) &&
              owner
            ) {
              await updateDomainsPayload({
                owner,
                create: isPayloadInvalid || hasCreateDraft,
                transfer: isPayloadInvalid || hasTransferDraft,
              });
            }

            cleanUpAllDrafts();

            navigate(`/cart/${cartId}/overview`);
          } catch (err) {
            setIsWorking(false);
            notifyDanger(__('Unexpected error occurred'));
            logger.error({ err }, 'Unable to update products payload.');
          }
        }}
      >
        {__('Continue')}
      </Button>
    </Layout>
  );
}

// FIXME: no ownProps
Presenter.preFetch = (
  props: PreFetchArgs<{}, void, empty, FullState>,
): PreFetchReturn<> => {
  const { dispatch, getState } = props;

  const setupOwner = selectSetupOwner(getState());
  const hasDomainCreate = selectHasDomainCreate(getState());
  const hasDomainTransfer = selectHasDomainTransfer(getState());

  const shouldFetchDefaultTemplateCreate =
    hasDomainCreate &&
    selectShouldFetchOneDefaultTemplate(getState(), {
      productType: 'domain',
      process: 'create',
      sharingId: setupOwner,
    });

  const shouldFetchDefaultTemplateTransfer =
    hasDomainTransfer &&
    selectShouldFetchOneDefaultTemplate(getState(), {
      productType: 'domain',
      process: 'transfer',
      sharingId: setupOwner,
    });

  return Promise.allSettled([
    CountryLoader.preFetch(props),
    // $FlowFixMe[incompatible-call] compat PreFetchArgs
    // $FlowFixMe[incompatible-variance]
    // $FlowFixMe[prop-missing]
    // $FlowFixMe[incompatible-exact]
    // $FlowFixMe[incompatible-use]
    // $FlowFixMe[not-an-object]
    Template.preFetch(props),
    // Preload contacts to compare and apply
    dispatch(loadFavoriteContacts(setupOwner)),
    // Preload extra params to compare and apply
    dispatch(contactbook.fetchOwnerContactExtraParams(setupOwner)),
    shouldFetchDefaultTemplateCreate &&
      dispatch(
        templateActions.fetchDefaultTemplate({
          productType: 'domain',
          process: 'create',
          sharingId: setupOwner,
        }),
      ),
    shouldFetchDefaultTemplateTransfer &&
      dispatch(
        templateActions.fetchDefaultTemplate({
          productType: 'domain',
          process: 'transfer',
          sharingId: setupOwner,
        }),
      ),
  ]);
};

export default Presenter;
